import { useEffect } from "react";
import { useLocation } from "react-router";
import { getPageDescription, getPageTitle } from "./pageDescriptions";
import { getPageImage } from "./pageImages";

// Every page component writes its own <title>, description and canonical in
// its own effect. None of them write Open Graph or Twitter Card tags, so a
// link to any page used to unfurl as a bare URL in LinkedIn, WhatsApp, Slack
// and X. Rather than edit ~50 page files, this hook runs once in Layout (a
// parent, so its effect fires after the page's) and mirrors whatever the page
// already set into the social tags.
//
// Pages are lazy-loaded, so on a cold load the page's effect can land *after*
// Layout's. A MutationObserver on <head> re-syncs whenever the title or
// description changes, which also covers the prerender pass (Puppeteer waits
// for network idle, by which point the observer has run).

const ORIGIN = "https://inqmisetechnologies.com";
const SITE_NAME = "InQmise Technologies";
const MARKER = "data-social-meta";
const BREADCRUMB_MARKER = "data-breadcrumbs";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    tag.setAttribute(MARKER, "");
    document.head.appendChild(tag);
  }
  if (tag.getAttribute("content") !== content) tag.setAttribute("content", content);
}

// Page effects append a fresh description meta on every mount instead of
// updating the existing one, so SPA navigation leaves a trail of stale
// descriptions behind. Keep the newest, drop the rest, and hand it back.
function resolveDescription(pathname) {
  const tags = [...document.head.querySelectorAll('meta[name="description"]')];
  const filled = tags.filter((tag) => (tag.getAttribute("content") || "").trim());
  const keep = filled[filled.length - 1] || tags[0] || null;
  for (const tag of tags) if (tag !== keep) tag.remove();

  // A page whose own description is too short for a search snippet gets the
  // written-for-search one instead (see pageDescriptions.js).
  const override = getPageDescription(pathname);
  if (override && keep && keep.getAttribute("content") !== override) {
    keep.setAttribute("content", override);
  }
  if (override) return override;
  return keep ? (keep.getAttribute("content") || "").trim() : "";
}

// Every indexable page canonicalises to itself (checked across all 63 sitemap
// URLs — none points elsewhere). Two pages, /faq and
// /corporate-social-responsibility, never set a canonical at all. And because
// pages add the tag without removing it, SPA navigation into such a page left
// the previous page's canonical in place — telling Google this URL is a
// duplicate of wherever the visitor came from. So: if the tag is missing, or
// names a different path than the one on screen, point it at this page.
function resolveCanonical(pathname) {
  // Draft previews and the admin are private, noindexed pages: they get no
  // canonical at all, rather than one naming a token URL.
  if (/^\/(preview|admin)(\/|$)/.test(pathname)) {
    document.head.querySelector('link[rel="canonical"]')?.remove();
    return `${ORIGIN}${pathname}`;
  }
  const self = pathname === "/" ? ORIGIN : `${ORIGIN}${pathname.replace(/\/+$/, "")}`;
  let link = document.head.querySelector('link[rel="canonical"]');
  const href = link?.getAttribute("href");
  const samePath = (() => {
    if (!href) return false;
    try {
      const a = new URL(href, ORIGIN).pathname.replace(/\/+$/, "") || "/";
      const b = pathname.replace(/\/+$/, "") || "/";
      return a === b;
    } catch {
      return false;
    }
  })();
  if (samePath) return href;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", self);
  return self;
}


// Breadcrumbs. Google shows a path instead of a raw URL when a page carries
// BreadcrumbList markup. Twenty-two pages had none — every case study, and ten
// service pages whose own effect never wrote one — so it is done here, once,
// for any page that has not written its own.
const COMPANY_PAGES = new Set([
  "/about", "/career", "/contact", "/faq", "/our-core-team",
  "/how-we-work-and-function", "/technologies", "/blog", "/services",
  "/case-studies", "/ai-solutions", "/corporate-social-responsibility",
]);

// The page's name in the trail. Titles are written for search results
// ("Mobile App Development Company in India | InQmise") and read as clutter in
// a breadcrumb, so the label comes from the URL instead, with the acronyms
// spelled the way we write them everywhere else.
const CRUMB_LABELS = {
  "/case-studies/ezrater": "EZRater",
  "/case-studies/three-lock-box": "Three Lock Box",
  "/case-studies/goal-envision": "GoalEnvision",
  "/case-studies/gym-chain-integration": "Gym Chain Integration",
  "/case-studies/ai-style-recommendation": "AI Style Recommendation",
  "/ui-ux-design": "UI/UX Design",
  "/ci-cd-pipelines": "CI/CD Pipelines",
  "/cloud-security-facet": "Cloud Security",
  "/qa-and-testing": "QA and Testing",
  "/faq": "FAQ",
  "/blog": "Blog",
  "/how-we-work-and-function": "How We Work",
  "/corporate-social-responsibility": "Corporate Social Responsibility",
};

const CRUMB_WORDS = {
  ai: "AI", api: "API", llm: "LLM", it: "IT", qa: "QA", erp: "ERP", crm: "CRM",
  saas: "SaaS", ios: "iOS", bi: "BI", vapt: "VAPT", devops: "DevOps", ui: "UI", ux: "UX",
};

function crumbName(path) {
  if (CRUMB_LABELS[path]) return CRUMB_LABELS[path];
  const slug = path.split("/").filter(Boolean).pop() || "";
  const label = slug
    .split("-")
    .map((word) => CRUMB_WORDS[word] || word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return label || (document.title || "").split("|")[0].trim();
}

function syncBreadcrumbs(pathname) {
  if (pathname === "/" || /^\/(preview|admin)(\/|$)/.test(pathname)) return;
  const existing = [...document.head.querySelectorAll('script[type="application/ld+json"]')];
  const ours = existing.find((tag) => tag.hasAttribute(BREADCRUMB_MARKER));
  // A page that ships its own breadcrumbs keeps them.
  if (existing.some((tag) => !tag.hasAttribute(BREADCRUMB_MARKER) && tag.textContent.includes('"BreadcrumbList"'))) {
    ours?.remove();
    return;
  }

  const path = pathname.replace(/\/+$/, "") || "/";
  const trail = [{ name: "Home", item: ORIGIN }];
  if (path.startsWith("/case-studies/")) trail.push({ name: "Case Studies", item: `${ORIGIN}/case-studies` });
  else if (!COMPANY_PAGES.has(path)) trail.push({ name: "Services", item: `${ORIGIN}/services` });
  trail.push({ name: crumbName(path), item: `${ORIGIN}${path}` });

  const json = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      item: crumb.item,
    })),
  });
  if (ours) {
    if (ours.textContent !== json) ours.textContent = json;
    return;
  }
  const tag = document.createElement("script");
  tag.type = "application/ld+json";
  tag.setAttribute(BREADCRUMB_MARKER, "");
  tag.textContent = json;
  document.head.appendChild(tag);
}

function syncSocialMeta(pathname) {
  // The keyword-targeted title, when this route has one, wins over the page's.
  const override = getPageTitle(pathname);
  if (override && document.title !== override) document.title = override;
  const title = (document.title || "").trim();
  if (!title) return;
  const description = resolveDescription(pathname);
  const url = resolveCanonical(pathname);
  const type = pathname.startsWith("/blog/") ? "article" : "website";

  upsertMeta("property", "og:site_name", SITE_NAME);
  upsertMeta("property", "og:type", type);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", url);
  // Each money page has its own branded card; everything else falls back to
  // og-default.png. A per-page image is what gives Google Images something
  // page-specific to show, instead of one card repeated across the site.
  const image = getPageImage(pathname);
  upsertMeta("property", "og:image", `${ORIGIN}${image.url}`);
  upsertMeta("property", "og:image:width", "1200");
  upsertMeta("property", "og:image:height", "630");
  upsertMeta("property", "og:image:alt", image.alt);
  upsertMeta("property", "og:locale", "en_IN");

  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", `${ORIGIN}${image.url}`);
  upsertMeta("name", "twitter:image:alt", image.alt);

  syncBreadcrumbs(pathname);
}

export default function useSocialMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    syncSocialMeta(pathname);

    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        syncSocialMeta(pathname);
      });
    });
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [pathname]);
}
